// EN TRÁNSITO (script 392/393): las vistas del flujo envío → tránsito → recepción.
// - RecepcionesPendientes: lo que le debe llegar al local; conteo CIEGO SUAVE
//   (ve productos/lotes pero no cantidades si la config lo exige), escaneo/desde
//   teclado, diferencias con motivo al cerrar. Lo contado bueno entra YA.
// - TransitosAbiertos: control del dueño — tránsitos con días/valor/atraso,
//   diferencias pendientes con resolución caso a caso (admin), retorno y
//   cierre forzado (válvula contra transferencias fantasma).
// - La configuración (flag requiere_recepcion + ciega + lead/atraso) es pantalla
//   propia: /inventario/transferencias-config (features/inventario/TransferenciasConfig.tsx).
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, FileText, PackageCheck, RotateCcw, Settings2, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { useMenus } from '@/lib/hooks';
import { useTabsStore } from '@/stores/tabs.store';
import { Campo, Modal, fieldCls, invRpc, invalidateInventario, num } from './shared';
import { LOCALE } from '@/lib/utils/regional';

const RUTA_CONFIG = '/inventario/transferencias-config';

// Deep-link a la pantalla de configuración, solo si el usuario tiene acceso (menú)
function useTieneAccesoConfig(): boolean {
  const { data: menus } = useMenus();
  if (!menus) return false;
  return menus.menus.some((m) => m.submenus.some((s) => s.opciones.some((o) => o.ruta === RUTA_CONFIG)));
}

export interface TransfPendiente {
  id: string; codigo: string; estado: string; origen: string; destino: string;
  location_destino_id: string; enviada_at: string; fecha_estimada: string | null;
  dias_transito: number; atrasada: boolean; total_lineas: number; total_unidades: number;
  total_costo: number; observaciones: string | null;
}
interface DetalleLinea {
  linea_id: string; producto_id: string; codigo: string; nombre: string;
  codigo_barras: string | null; unidad_medida: string | null;
  lote_id: string | null; lote: string | null; fecha_caducidad: string | null;
  cantidad_enviada: number | null;   // null = recepción ciega
}
interface Detalle {
  id: string; codigo: string; estado: string; origen: string; destino: string;
  enviada_at: string; fecha_estimada: string | null; recepcion_ciega: boolean; lineas: DetalleLinea[];
}
export interface TransitoAbierto {
  id: string; codigo: string; estado: string; origen: string; destino: string;
  enviada_at: string; fecha_estimada: string | null; dias_transito: number;
  atrasada: boolean; valor_pendiente: number;
}
export interface Diferencia {
  id: string; transferencia_id: string; codigo: string; origen: string; destino: string;
  producto: string; lote: string | null; cantidad: number; valor: number;
  motivo: string; notas: string | null; antiguedad_dias: number;
}
export interface TransfConfig {
  requiere_recepcion: boolean; recepcion_ciega: boolean; emite_guia: boolean;
  margen_caducidad_dias: number; lead_default_dias: number; atraso_alerta_dias: number;
  bloqueo_caja_dias: number | null;   // 910: NULL = sin bloqueo de caja
}

const fmtF = (v?: string | null) => (v ? new Date(v.includes('T') ? v : `${v}T00:00:00`).toLocaleDateString(LOCALE, { day: '2-digit', month: 'short' }) : '—');
const MOTIVOS = [['faltante', 'Faltante'], ['danado', 'Dañado'], ['vencido', 'Vencido'], ['otro', 'Otro']] as const;

export const usePendientes = () => useQuery<TransfPendiente[]>({
  queryKey: ['inv-transf-pendientes'],
  queryFn: () => invRpc<TransfPendiente[]>('inv_transf_pendientes', { _location: null }),
  refetchInterval: 60000,
});

// ---------------------------------------------------------------------------
// Recepciones pendientes (el "camión por llegar" del jefe de local)
// ---------------------------------------------------------------------------
export function RecepcionesPendientes() {
  const { data: pendientes = [], isLoading } = usePendientes();
  // 910: si la empresa fijó bloqueo_caja_dias, la caja del local no abre con estas sin recibir
  const { data: cfg } = useQuery<TransfConfig>({
    queryKey: ['inv-transf-config'], queryFn: () => invRpc<TransfConfig>('inv_transf_config_get', {}),
  });
  const bloqueo = cfg?.bloqueo_caja_dias ?? null;
  const [recibiendo, setRecibiendo] = useState<string | null>(null);

  if (isLoading) return <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>;
  if (pendientes.length === 0) {
    return <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">No hay transferencias en camino hacia tus bodegas.</CardContent></Card>;
  }
  return (
    <div className="space-y-3">
      {pendientes.map((t) => (
        <Card key={t.id}>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-3">
            <div className="text-sm">
              <div className="font-semibold">{t.codigo} <span className="font-normal text-muted-foreground">· {t.origen} → {t.destino}</span>
                {t.atrasada && <span className="ml-2 animate-pulse rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">ATRASADA</span>}
                {t.estado === 'recibido_parcial' && <span className="ml-2 rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">recepción parcial</span>}
                {bloqueo != null && (t.dias_transito > bloqueo
                  ? <span className="ml-2 rounded bg-red-600 px-2 py-0.5 text-xs font-semibold text-white">LA CAJA DEL LOCAL NO ABRE HASTA RECIBIRLA</span>
                  : <span className="ml-2 rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                      bloquea la caja en {bloqueo - t.dias_transito + 1} día(s)</span>)}
              </div>
              <div className="text-xs text-muted-foreground">
                Salió {fmtF(t.enviada_at)} · llega ~{fmtF(t.fecha_estimada)} · {t.dias_transito} día(s) en tránsito · {t.total_lineas} línea(s)
              </div>
            </div>
            <Button size="sm" onClick={() => setRecibiendo(t.id)}><PackageCheck className="w-4 h-4 mr-1" /> Recibir</Button>
          </CardContent>
        </Card>
      ))}
      {recibiendo && <ModalRecibir transferenciaId={recibiendo} onClose={() => setRecibiendo(null)} />}
    </div>
  );
}

// Conteo: escaneo por código de barras o digitación; ciega suave según config
function ModalRecibir({ transferenciaId, onClose }: { transferenciaId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: det } = useQuery<Detalle>({
    queryKey: ['inv-transf-det', transferenciaId],
    queryFn: () => invRpc<Detalle>('inv_transf_recibir_detalle', { _transferencia: transferenciaId }),
  });
  const [conteo, setConteo] = useState<Record<string, number>>({});
  const [motivos, setMotivos] = useState<Record<string, string>>({});
  const [scan, setScan] = useState('');
  const [multiplo, setMultiplo] = useState(1);
  const [fase, setFase] = useState<'conteo' | 'cerrar'>('conteo');
  // 910: cerrar con faltantes exige explicar qué pasó; la base dice cuánto falta
  const [obs, setObs] = useState('');
  const [faltaObs, setFaltaObs] = useState<string | null>(null);

  const setC = (lineaId: string, v: number) => setConteo((c) => ({ ...c, [lineaId]: Math.max(v, 0) }));

  const bip = () => {
    const code = scan.trim();
    if (!code || !det) return;
    const l = det.lineas.find((x) => x.codigo_barras === code || x.codigo === code);
    if (!l) { toast.error(`"${code}" no está en este envío — si llegó de verdad, regístralo como ajuste aparte`); setScan(''); return; }
    setC(l.linea_id, (conteo[l.linea_id] ?? 0) + Math.max(multiplo, 1));
    setScan('');
  };

  const recibir = useMutation({
    mutationFn: (cerrar: boolean) => invRpc('inv_transferencia_recibir', {
      _transferencia: transferenciaId,
      _lineas: (det?.lineas ?? []).map((l) => ({
        linea_id: l.linea_id, cantidad: conteo[l.linea_id] ?? 0,
        motivo_diferencia: motivos[l.linea_id] || null,
      })),
      _cerrar: cerrar, _observaciones: cerrar && obs.trim() ? obs.trim() : null,
    }),
    onSuccess: (r: unknown) => {
      const d = r as { recibidas: number; diferencias: number; estado: string };
      toast.success(`Recepción registrada: ${num(d.recibidas, 2)} unidades${Number(d.diferencias) > 0 ? ` · ${d.diferencias} diferencia(s) por resolver` : ''}`);
      invalidateInventario(qc);
      void qc.invalidateQueries({ queryKey: ['inv-transf-pendientes'] });
      onClose();
    },
    onError: (e) => {
      const msg = getErrorMessage(e);
      if (/Explique qué pasó/i.test(msg)) setFaltaObs(msg); else toast.error(msg);
    },
  });

  const recibirCompleto = useMutation({
    mutationFn: () => invRpc('inv_transferencia_recibir_completo', { _transferencia: transferenciaId, _observaciones: null }),
    onSuccess: (r: unknown) => {
      const d = r as { recibidas: number };
      toast.success(`Recepción completa: ${num(d.recibidas, 2)} unidad(es) recibida(s) tal cual se enviaron`);
      invalidateInventario(qc);
      void qc.invalidateQueries({ queryKey: ['inv-transf-pendientes'] });
      onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const totalContado = useMemo(() => Object.values(conteo).reduce((s, v) => s + v, 0), [conteo]);

  if (!det) return null;
  return (
    <Modal title={`Recibir ${det.codigo} — ${det.origen} → ${det.destino}`} onClose={onClose} wide>
      {dlg.ui}
      <div className="space-y-3 text-sm">
        {det.recepcion_ciega && fase === 'conteo' && (
          <div className="rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:border-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
            Recepción a ciegas: cuenta lo que llegó físicamente — las cantidades enviadas no se muestran. El sistema compara al cerrar.
          </div>
        )}
        <div className="flex flex-wrap items-end gap-2">
          <Campo label="Escanear código de barras (Enter suma)">
            <input className={fieldCls} autoFocus value={scan} onChange={(e) => setScan(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); bip(); } }} placeholder="Escanea o digita el código…" />
          </Campo>
          <Campo label="× por bip (bultos)">
            <input type="number" min={1} className={`${fieldCls} w-24`} value={multiplo} onChange={(e) => setMultiplo(Math.max(Number(e.target.value) || 1, 1))} />
          </Campo>
          <div className="ml-auto text-xs text-muted-foreground">Contado: <span className="font-semibold text-foreground">{num(totalContado, 2)}</span> unidades</div>
        </div>

        <div className="max-h-[45vh] overflow-y-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted"><tr className="text-left text-xs text-muted-foreground">
              <th className="py-2 px-2">Producto</th><th className="py-2 px-2">Lote</th>
              {!det.recepcion_ciega && <th className="py-2 px-2 text-right">Enviado</th>}
              <th className="py-2 px-2 text-right w-28">Contado</th>
              {fase === 'cerrar' && <th className="py-2 px-2 w-36">Motivo si falta</th>}
            </tr></thead>
            <tbody>
              {det.lineas.map((l) => (
                <tr key={l.linea_id} className="border-t">
                  <td className="py-1.5 px-2"><span className="font-mono text-xs text-muted-foreground">{l.codigo}</span> {l.nombre}</td>
                  <td className="py-1.5 px-2 text-xs">{l.lote ?? '—'}{l.fecha_caducidad ? ` (vence ${fmtF(l.fecha_caducidad)})` : ''}</td>
                  {!det.recepcion_ciega && <td className="py-1.5 px-2 text-right tabular-nums">{num(Number(l.cantidad_enviada ?? 0), 2)}</td>}
                  <td className="py-1.5 px-2">
                    <input type="number" min={0} step="0.0001" className={`${fieldCls} h-8 text-right`}
                      value={conteo[l.linea_id] ?? ''} placeholder="0"
                      onChange={(e) => setC(l.linea_id, Number(e.target.value) || 0)} />
                  </td>
                  {fase === 'cerrar' && (
                    <td className="py-1.5 px-2">
                      <select className={`${fieldCls} h-8 text-xs`} value={motivos[l.linea_id] ?? 'faltante'}
                        onChange={(e) => setMotivos((m) => ({ ...m, [l.linea_id]: e.target.value }))}>
                        {MOTIVOS.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
                      </select>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t pt-3">
          <Button variant="outline" className="mr-auto text-emerald-700 dark:text-emerald-400"
            isLoading={recibirCompleto.isPending}
            title="Recibe TODO tal cual se envió, sin contar ni generar diferencias"
            onClick={async () => { if (await dlg.confirmar('¿Recibir TODO completo, tal cual se envió, sin contar?', { detalle: 'Entra todo al stock y no genera diferencias.' })) recibirCompleto.mutate(); }}>
            <PackageCheck className="w-4 h-4 mr-1" /> Recibir todo completo (sin contar)
          </Button>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button variant="outline" isLoading={recibir.isPending} disabled={totalContado <= 0}
            title="Registra lo contado y deja el resto EN TRÁNSITO (llega otro camión después)"
            onClick={() => recibir.mutate(false)}>Recibir parcial (queda en tránsito)</Button>
          {fase === 'conteo'
            ? <Button disabled={totalContado <= 0} onClick={() => setFase('cerrar')}
                title={totalContado <= 0 ? 'No has contado nada: no se puede cerrar una recepción en cero' : undefined}>Cerrar conteo…</Button>
            : <Button isLoading={recibir.isPending} disabled={totalContado <= 0}
                title="Lo contado entra al stock; lo no contado queda como DIFERENCIA por resolver"
                onClick={() => recibir.mutate(true)}>Confirmar recepción</Button>}
        </div>
        {totalContado <= 0 && (
          <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
            Aún no has contado nada. Si llegó todo, usa <strong>Recibir todo completo</strong>. Si no llegó, cierra esta
            ventana sin confirmar: la transferencia sigue en tránsito y el administrador decide.
          </p>
        )}
        {fase === 'cerrar' && (
          <div className="space-y-2">
            {faltaObs && (
              <div className="flex items-start gap-2 rounded-md border border-red-400 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span>{faltaObs}</span>
              </div>
            )}
            <Campo label="Observación (obligatoria si falta algo)">
              <textarea className={`${fieldCls} h-auto`} rows={2} value={obs} autoFocus={!!faltaObs}
                placeholder="Qué pasó con lo que no llegó: caja rota, vino menos, se devolvió al chofer…"
                onChange={(e) => setObs(e.target.value)} />
            </Campo>
            <p className="text-xs text-muted-foreground">Al confirmar: lo contado entra al stock YA; lo que falte queda como diferencia con su motivo (la resuelve un administrador). Nada bloquea la percha.</p>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Tránsitos abiertos + diferencias pendientes (control / antifraude)
// ---------------------------------------------------------------------------
interface Abiertos { abiertos: TransitoAbierto[]; diferencias_pendientes: Diferencia[] }

export function TransitosAbiertos() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data, isLoading } = useQuery<Abiertos>({
    queryKey: ['inv-transitos-abiertos'], queryFn: () => invRpc<Abiertos>('inv_transitos_abiertos', {}),
  });
  const tieneAccesoConfig = useTieneAccesoConfig();
  const openTab = useTabsStore((s) => s.openTab);
  const irAConfig = () => openTab({ id: RUTA_CONFIG, title: 'Configuración de transferencias', path: RUTA_CONFIG });
  const refrescar = () => { invalidateInventario(qc); void qc.invalidateQueries({ queryKey: ['inv-transitos-abiertos'] }); void qc.invalidateQueries({ queryKey: ['inv-transf-pendientes'] }); };

  const retornar = useMutation({
    mutationFn: (id: string) => invRpc('inv_transferencia_retornar', { _transferencia: id, _motivo: 'Retorno al origen (no llegó al destino)' }),
    onSuccess: () => { toast.success('Saldo retornado al origen'); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const forzar = useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      invRpc('inv_transferencia_cerrar_forzado', { _transferencia: id, _motivo: motivo }),
    onSuccess: () => { toast.success('Transferencia cerrada — el saldo quedó como diferencias por resolver'); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const pedirForzar = async (id: string) => {
    const v = await dlg.pedir('Cierre forzado', [{ key: 'motivo', label: 'Motivo del cierre forzado (obligatorio)', minimo: 3 }]);
    if (v) forzar.mutate({ id, motivo: v.motivo.trim() });
  };
  const resolver = useMutation({
    mutationFn: ({ id, resolucion }: { id: string; resolucion: string }) =>
      invRpc('inv_transf_diferencia_resolver', { _id: id, _resolucion: resolucion, _notas: null }),
    onSuccess: () => { toast.success('Diferencia resuelta'); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading) return <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>;
  const abiertos = data?.abiertos ?? [];
  const difs = data?.diferencias_pendientes ?? [];
  const valorTransito = abiertos.reduce((s, t) => s + Number(t.valor_pendiente || 0), 0);

  return (
    <div className="space-y-4">
      {dlg.ui}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-4 text-sm">
          <span><Truck className="mr-1 inline w-4 h-4 text-muted-foreground" /> {abiertos.length} en tránsito · <span className="font-semibold tabular-nums">${num(valorTransito, 2)}</span> en camiones</span>
          {abiertos.some((t) => t.atrasada) && <span className="text-red-600 dark:text-red-400 font-medium"><AlertTriangle className="mr-1 inline w-4 h-4" /> {abiertos.filter((t) => t.atrasada).length} atrasada(s)</span>}
          {difs.length > 0 && <span className="text-amber-600 dark:text-amber-400">{difs.length} diferencia(s) por resolver (${num(difs.reduce((s, d) => s + Number(d.valor || 0), 0), 2)})</span>}
        </div>
        {tieneAccesoConfig && (
          <Button size="sm" variant="outline" onClick={irAConfig}><Settings2 className="w-4 h-4 mr-1" /> Configuración</Button>
        )}
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Tránsitos abiertos</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">N°</th><th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Ruta</th>
              <th className="py-2 pr-3">Salió</th><th className="py-2 pr-3">ETA</th><th className="py-2 pr-3 text-right">Días</th>
              <th className="py-2 pr-3 text-right">Valor pendiente</th><th className="py-2 pr-3">Estado</th><th className="py-2 pr-3">Guía SRI</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {abiertos.length === 0 && <tr><td colSpan={10} className="py-6 text-center text-muted-foreground">Sin tránsitos abiertos — todo recibido ✓</td></tr>}
              {abiertos.map((t, i) => (
                <tr key={t.id} className={`border-b last:border-0 ${t.atrasada ? 'bg-red-50/50 dark:bg-red-900/10' : ''}`}>
                  <td className="py-2 pr-3 text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 font-mono">{t.codigo}</td>
                  <td className="py-2 pr-3">{t.origen} → {t.destino}</td>
                  <td className="py-2 pr-3">{fmtF(t.enviada_at)}</td>
                  <td className="py-2 pr-3">{fmtF(t.fecha_estimada)}{t.atrasada && <AlertTriangle className="ml-1 inline w-3.5 h-3.5 text-red-500" />}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{t.dias_transito}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">${num(Number(t.valor_pendiente), 2)}</td>
                  <td className="py-2 pr-3 text-xs capitalize">{t.estado.replace('_', ' ')}</td>
                  <td className="py-2 pr-3"><GuiaCelda transferenciaId={t.id} onCambio={refrescar} /></td>
                  <td className="py-2 whitespace-nowrap text-right">
                    <Button size="sm" variant="outline" className="mr-1" isLoading={retornar.isPending}
                      title="El saldo vuelve al origen (regresó en el camión / nunca salió)"
                      onClick={async () => { if (await dlg.confirmar(`¿Retornar el saldo de ${t.codigo} a ${t.origen}?`)) retornar.mutate(t.id); }}>
                      <RotateCcw className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="outline" className="text-red-500" isLoading={forzar.isPending}
                      title="Cierre forzado (admin): el saldo pasa a diferencias por resolver"
                      onClick={() => pedirForzar(t.id)}>Cerrar</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Diferencias por resolver</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">TRF</th><th className="py-2 pr-3">Ruta</th><th className="py-2 pr-3">Producto</th>
              <th className="py-2 pr-3">Lote</th><th className="py-2 pr-3 text-right">Cant.</th><th className="py-2 pr-3 text-right">Valor</th>
              <th className="py-2 pr-3">Motivo</th><th className="py-2 pr-3 text-right">Días</th><th className="py-2 w-56">Resolución</th>
            </tr></thead>
            <tbody>
              {difs.length === 0 && <tr><td colSpan={9} className="py-6 text-center text-muted-foreground">Sin diferencias pendientes <CheckCircle2 className="ml-1 inline w-4 h-4 text-emerald-500" /></td></tr>}
              {difs.map((d) => (
                <tr key={d.id} className={`border-b last:border-0 ${d.antiguedad_dias > 3 ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}`}>
                  <td className="py-2 pr-3 font-mono">{d.codigo}</td>
                  <td className="py-2 pr-3 text-xs">{d.origen} → {d.destino}</td>
                  <td className="py-2 pr-3">{d.producto}</td>
                  <td className="py-2 pr-3 text-xs">{d.lote ?? '—'}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{num(Number(d.cantidad), 2)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">${num(Number(d.valor), 2)}</td>
                  <td className="py-2 pr-3 text-xs capitalize" title={d.notas ?? ''}>{d.motivo}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{d.antiguedad_dias}</td>
                  <td className="py-2">
                    <select className={`${fieldCls} h-8 text-xs`} value="" disabled={resolver.isPending}
                      onChange={async (e) => {
                        const valor = e.target.value; const texto = e.target.options[e.target.selectedIndex].text;
                        if (valor && await dlg.confirmar(`¿Resolver como "${texto}"?`)) resolver.mutate({ id: d.id, resolucion: valor });
                      }}>
                      <option value="">Resolver como…</option>
                      <option value="merma_origen">Merma del origen</option>
                      <option value="cargo_transportista">Cargo al transportista</option>
                      <option value="entrada_destino">Apareció (entra al destino)</option>
                      <option value="retorno_origen">Retorno al origen</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

    </div>
  );
}

// ---------------------------------------------------------------------------
// Guía de remisión SRI por transferencia (código 06) — desacoplada del envío
// ---------------------------------------------------------------------------
interface GuiaInfo { id: string; estado_emision: string; serie_comprobante: string | null; ride_url: string | null; mensaje_sri: string | null }
const GUIA_LBL: Record<string, { t: string; cls: string }> = {
  por_emitir: { t: 'Por emitir', cls: 'bg-muted text-muted-foreground' },
  encolado: { t: 'Encolada', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  procesando: { t: 'Procesando', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  autorizado: { t: 'Autorizada', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  rechazado: { t: 'Rechazada', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  devuelto: { t: 'Devuelta', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  error: { t: 'Error', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
};

function GuiaCelda({ transferenciaId, onCambio }: { transferenciaId: string; onCambio: () => void }) {
  const qc = useQueryClient();
  const { data: guia } = useQuery<GuiaInfo | null>({
    queryKey: ['inv-guia', transferenciaId],
    queryFn: () => invRpc<GuiaInfo | null>('inv_guia_de_transferencia', { _transferencia: transferenciaId }),
    refetchInterval: (q) => (['autorizado', 'rechazado', 'error', 'devuelto'].includes(q.state.data?.estado_emision ?? '') ? false : 8000),
  });
  const [emitir, setEmitir] = useState(false);
  const reemitir = useMutation({
    mutationFn: () => invRpc('inv_guia_reemitir', { _guia: guia!.id }),
    onSuccess: () => { toast.success('Guía re-encolada'); void qc.invalidateQueries({ queryKey: ['inv-guia', transferenciaId] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!guia) {
    return (
      <>
        <Button size="sm" variant="outline" onClick={() => setEmitir(true)}><FileText className="w-3.5 h-3.5 mr-1" /> Emitir guía</Button>
        {emitir && <ModalEmitirGuia transferenciaId={transferenciaId} onClose={() => setEmitir(false)} onEmitida={() => { setEmitir(false); void qc.invalidateQueries({ queryKey: ['inv-guia', transferenciaId] }); onCambio(); }} />}
      </>
    );
  }
  const e = GUIA_LBL[guia.estado_emision] ?? GUIA_LBL.por_emitir;
  return (
    <div className="flex items-center gap-1.5">
      <span className={`rounded px-2 py-0.5 text-xs ${e.cls}`} title={guia.mensaje_sri ?? ''}>{e.t}</span>
      {guia.serie_comprobante && <span className="text-xs text-muted-foreground">{guia.serie_comprobante}</span>}
      {guia.ride_url && <a href={`/sri-docs/${guia.ride_url.split('/').slice(-2).join('/')}`} target="_blank" rel="noopener noreferrer" className="text-primary" title="Ver RIDE"><FileText className="w-3.5 h-3.5" /></a>}
      {['rechazado', 'error', 'devuelto'].includes(guia.estado_emision) && (
        <Button size="sm" variant="outline" isLoading={reemitir.isPending} onClick={() => reemitir.mutate()}>Reintentar</Button>
      )}
    </div>
  );
}

interface Transportista { id: string; tipo_identificacion: string; identificacion: string; razon_social: string; placa: string | null; propio: boolean }
function ModalEmitirGuia({ transferenciaId, onClose, onEmitida }: { transferenciaId: string; onClose: () => void; onEmitida: () => void }) {
  const { data: transportistas = [] } = useQuery<Transportista[]>({
    queryKey: ['fin-transportistas'], queryFn: () => invRpc<Transportista[]>('fin_transportistas', {}),
  });
  const [modo, setModo] = useState<'catalogo' | 'nuevo'>('catalogo');
  const [transportistaId, setTransportistaId] = useState('');
  const [nuevo, setNuevo] = useState({ tipo_identificacion: '04', identificacion: '', razon_social: '', placa: '', propio: true });
  const [placa, setPlaca] = useState('');
  const [motivo, setMotivo] = useState('Transferencia entre establecimientos del mismo contribuyente');

  const emitir = useMutation({
    mutationFn: async () => {
      let tId = transportistaId;
      if (modo === 'nuevo') {
        if (!nuevo.identificacion || !nuevo.razon_social) throw new Error('Identificación y razón social del transportista son obligatorias');
        const r = await invRpc<{ id: string }>('fin_transportista_guardar', { _data: nuevo });
        tId = r.id;
      }
      const placaFinal = placa || nuevo.placa || transportistas.find((t) => t.id === tId)?.placa || '';
      if (!placaFinal) throw new Error('La placa del vehículo es obligatoria');
      return invRpc('inv_transferencia_emitir_guia', {
        _transferencia: transferenciaId,
        _datos: { transportista_id: tId || null, placa: placaFinal, motivo_traslado: motivo },
      });
    },
    onSuccess: () => { toast.success('Guía de remisión encolada al SRI'); onEmitida(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal title="Emitir guía de remisión (SRI cód. 06)" onClose={onClose}>
      <div className="space-y-3 text-sm">
        <div className="flex gap-2">
          <button type="button" onClick={() => setModo('catalogo')} className={`rounded px-3 py-1 text-sm ${modo === 'catalogo' ? 'bg-primary text-primary-foreground' : 'border text-muted-foreground'}`}>Transportista guardado</button>
          <button type="button" onClick={() => setModo('nuevo')} className={`rounded px-3 py-1 text-sm ${modo === 'nuevo' ? 'bg-primary text-primary-foreground' : 'border text-muted-foreground'}`}>Nuevo</button>
        </div>
        {modo === 'catalogo' ? (
          <Campo label="Transportista">
            <select className={fieldCls} value={transportistaId} onChange={(e) => setTransportistaId(e.target.value)}>
              <option value="">— Transporte propio (la empresa) —</option>
              {transportistas.map((t) => <option key={t.id} value={t.id}>{t.razon_social} · {t.identificacion}{t.placa ? ` · ${t.placa}` : ''}</option>)}
            </select>
          </Campo>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <Campo label="Tipo id.">
              <select className={fieldCls} value={nuevo.tipo_identificacion} onChange={(e) => setNuevo({ ...nuevo, tipo_identificacion: e.target.value })}>
                <option value="04">RUC</option><option value="05">Cédula</option><option value="06">Pasaporte</option>
              </select>
            </Campo>
            <Campo label="Identificación"><input className={fieldCls} value={nuevo.identificacion} onChange={(e) => setNuevo({ ...nuevo, identificacion: e.target.value })} /></Campo>
            <Campo label="Razón social" ><input className={fieldCls} value={nuevo.razon_social} onChange={(e) => setNuevo({ ...nuevo, razon_social: e.target.value })} /></Campo>
            <Campo label="Placa"><input className={fieldCls} value={nuevo.placa} onChange={(e) => setNuevo({ ...nuevo, placa: e.target.value })} /></Campo>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Campo label="Placa del vehículo *"><input className={fieldCls} placeholder="PBA-1234" value={placa} onChange={(e) => setPlaca(e.target.value)} /></Campo>
          <Campo label="Motivo del traslado"><input className={fieldCls} value={motivo} onChange={(e) => setMotivo(e.target.value)} /></Campo>
        </div>
        <p className="text-xs text-muted-foreground">Transporte propio: deja el transportista en «propio» y solo pon la placa. La guía se emite al SRI en segundo plano (no bloquea el envío); el RIDE viaja con el vehículo.</p>
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={emitir.isPending} onClick={() => emitir.mutate()}>Emitir guía</Button>
        </div>
      </div>
    </Modal>
  );
}

